import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { InfoTip, Spinner, ToggleField } from '../../../shared/components';
import {
  createVoiceChannelAccount,
  getVoiceChannelAccountSettings,
  testVoiceChannelAccountConnection,
  updateVoiceChannelAccountCredentials,
} from '../services/channelAccountService';
import { ChannelAccountVoiceSettings, CallingConnectionTestResult } from '../types/channelAccount';
import { getInboundPopSettings, InboundPopSettings } from '../../calling/services/callingService';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';
import styles from './CallsConnectorTab.module.css';

const emptyVoiceSettingsForm = {
  displayName: '3CX',
  serverUrl: '',
  clientId: '',
  clientSecret: '',
  defaultCallerId: '',
  inboundEnabled: true,
  outboundEnabled: true,
  aiEnabled: false,
  aiAnswerMode: 'Off',
  aiAnsweredNumbers: '',
  handoffTarget: '',
  greeting: '',
  businessHoursStart: '09:00',
  businessHoursEnd: '18:00',
  businessHoursTimeZone: '',
  businessHoursWeekdays: '1,2,3,4,5',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioFromNumber: '',
  threeCxAiEnabled: false,
  threeCxRoutePointDn: '',
};

/** ISO weekday numbers, Monday = 1 … Sunday = 7, matching the backend parser. */
const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const parseWeekdays = (raw: string): Set<number> => {
  const days = new Set<number>();
  raw.split(/[,; ]+/).forEach(token => {
    const value = Number.parseInt(token, 10);
    if (value >= 1 && value <= 7) {
      days.add(value);
    }
  });
  return days;
};

const formatWeekdays = (days: Set<number>): string =>
  ISO_WEEKDAYS.filter(day => days.has(day)).join(',');

const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const getKnownTimeZones = (): string[] => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
};

type Tone = 'ok' | 'warn' | 'bad' | 'off';

const pillClass: Record<Tone, string> = {
  ok: styles.pillOk,
  warn: styles.pillWarn,
  bad: styles.pillBad,
  off: styles.pillOff,
};

const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`${styles.pill} ${pillClass[tone]}`}>{children}</span>
);

interface FieldProps {
  label: string;
  info?: string;
  hint?: string;
  className?: string;
  /** The control. A function receives the generated ids when the control is not a single element. */
  children: React.ReactElement | ((ids: { id: string; labelId: string; hintId: string }) => React.ReactNode);
}

/**
 * Label + optional info tooltip + control + optional hint. The label is linked with `htmlFor` instead of
 * wrapping the control, so the tooltip text is not read out as part of the control's name.
 */
const Field: React.FC<FieldProps> = ({ label, info, hint, className, children }) => {
  const id = React.useId();
  const labelId = React.useId();
  const hintId = React.useId();
  const control = typeof children === 'function'
    ? children({ id, labelId, hintId })
    : React.cloneElement(children as React.ReactElement<{ id?: string; 'aria-describedby'?: string }>, {
      id,
      'aria-describedby': hint ? hintId : undefined,
    });

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <span className={styles.labelRow}>
        <label id={labelId} htmlFor={id}>{label}</label>
        {info ? <InfoTip text={info} /> : null}
      </span>
      {control}
      {hint ? <span id={hintId} className="company-field-hint">{hint}</span> : null}
    </div>
  );
};

interface CallsConnectorTabProps {
  canManage: boolean;
}

export const CallsConnectorTab: React.FC<CallsConnectorTabProps> = ({ canManage }) => {
  const { t, i18n } = useTranslation(['companies', 'admin', 'calling', 'common']);
  const { showToast } = useToast();
  const voiceChannelAccounts = useChannelAccounts('Voice', true);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [testingConnection, setTestingConnection] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountVoiceSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState(emptyVoiceSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [latestTestResult, setLatestTestResult] = React.useState<CallingConnectionTestResult | null>(null);
  const [showAddAccount, setShowAddAccount] = React.useState(false);
  const [popSettings, setPopSettings] = React.useState<InboundPopSettings | null>(null);
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const knownTimeZones = React.useMemo(getKnownTimeZones, []);
  const weekdayLabels = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
    // 2024-01-01 is a Monday; step through the week so labels follow the active UI language.
    return ISO_WEEKDAYS.map(day => formatter.format(new Date(Date.UTC(2024, 0, day, 12))));
  }, [i18n.language]);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getVoiceChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestTestResult(null);
      setSettingsForm({
        displayName: '3CX',
        serverUrl: data.serverUrl || '',
        clientId: '',
        clientSecret: '',
        defaultCallerId: data.defaultCallerId || '',
        inboundEnabled: data.inboundEnabled,
        outboundEnabled: data.outboundEnabled,
        aiEnabled: Boolean(data.voiceAgent?.enabled),
        aiAnswerMode: data.voiceAgent?.answerMode || 'Off',
        aiAnsweredNumbers: data.voiceAgent?.answeredNumbers || '',
        handoffTarget: data.voiceAgent?.handoffTarget || '',
        greeting: data.voiceAgent?.greeting || '',
        businessHoursStart: data.voiceAgent?.businessHoursStart || '09:00',
        businessHoursEnd: data.voiceAgent?.businessHoursEnd || '18:00',
        businessHoursTimeZone: data.voiceAgent?.businessHoursTimeZone || '',
        businessHoursWeekdays: data.voiceAgent?.businessHoursWeekdays || '1,2,3,4,5',
        twilioAccountSid: '',
        twilioAuthToken: '',
        twilioFromNumber: data.voiceAgent?.twilioFromNumber || '',
        threeCxAiEnabled: Boolean(data.voiceAgent?.threeCxEnabled),
        threeCxRoutePointDn: data.voiceAgent?.threeCxRoutePointDn || '',
      });
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadVoiceSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = voiceChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSettingsForm(emptyVoiceSettingsForm);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, voiceChannelAccounts.selectedAccountId]);

  React.useEffect(() => {
    const accountId = voiceChannelAccounts.selectedAccountId;
    if (!accountId || !canManage) {
      setPopSettings(null);
      return;
    }

    let cancelled = false;
    // The secret is created on first read, so only ask for it when someone can actually configure 3CX.
    void getInboundPopSettings(accountId)
      .then(settings => {
        if (!cancelled) {
          setPopSettings(settings);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPopSettings(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canManage, voiceChannelAccounts.selectedAccountId]);

  const copyValue = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(current => (current === field ? null : current)), 2000);
    } catch {
      showToast(value, 'info');
    }
  };

  const saveSettings = async () => {
    const selectedAccount = voiceChannelAccounts.selectedAccount;
    const isInitialSetup = voiceChannelAccounts.accounts.length === 0;

    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (isInitialSetup && (!settingsForm.serverUrl.trim() || !settingsForm.clientId.trim() || !settingsForm.clientSecret.trim())) {
      const message = t('voiceCredentialsRequired');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      if (isInitialSetup) {
        await createVoiceChannelAccount({
          displayName: settingsForm.displayName.trim() || '3CX',
          serverUrl: settingsForm.serverUrl.trim(),
          clientId: settingsForm.clientId.trim(),
          clientSecret: settingsForm.clientSecret.trim(),
          inboundEnabled: settingsForm.inboundEnabled,
          outboundEnabled: settingsForm.outboundEnabled,
          defaultCallerId: settingsForm.defaultCallerId.trim() || undefined,
          isDefault: true,
        });
        await voiceChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateVoiceChannelAccountCredentials(selectedAccount.id, {
          serverUrl: settingsForm.serverUrl.trim() || undefined,
          clientId: settingsForm.clientId.trim() || undefined,
          clientSecret: settingsForm.clientSecret.trim() || undefined,
          inboundEnabled: settingsForm.inboundEnabled,
          outboundEnabled: settingsForm.outboundEnabled,
          defaultCallerId: settingsForm.defaultCallerId.trim() || undefined,
          voiceAgent: {
            enabled: settingsForm.aiEnabled,
            answerMode: settingsForm.aiEnabled ? settingsForm.aiAnswerMode : 'Off',
            answeredNumbers: settingsForm.aiAnsweredNumbers.trim(),
            handoffTarget: settingsForm.handoffTarget.trim(),
            greeting: settingsForm.greeting.trim(),
            businessHoursStart: settingsForm.businessHoursStart.trim(),
            businessHoursEnd: settingsForm.businessHoursEnd.trim(),
            businessHoursTimeZone: settingsForm.businessHoursTimeZone.trim(),
            businessHoursWeekdays: settingsForm.businessHoursWeekdays.trim(),
            twilioAccountSid: settingsForm.twilioAccountSid.trim() || undefined,
            twilioAuthToken: settingsForm.twilioAuthToken.trim() || undefined,
            twilioFromNumber: settingsForm.twilioFromNumber.trim(),
            threeCxEnabled: settingsForm.threeCxAiEnabled,
            threeCxRoutePointDn: settingsForm.threeCxRoutePointDn.trim(),
          },
        });
        await loadAccountSettings(selectedAccount.id);
        await voiceChannelAccounts.reload();
      }

      setSettingsForm(prev => ({
        ...prev,
        clientId: '',
        clientSecret: '',
        twilioAccountSid: '',
        twilioAuthToken: '',
      }));
      setSettingsNotice(t('voiceSettingsSaved'));
      showToast(t('voiceSettingsSaved'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveVoiceSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    const selectedAccount = voiceChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setTestingConnection(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await testVoiceChannelAccountConnection(selectedAccount.id);
      setLatestTestResult(result);
      if (result.succeeded) {
        setSettingsNotice(result.message || t('voiceConnectionSucceeded'));
        showToast(result.message || t('voiceConnectionSucceeded'), 'success');
      } else {
        const message = result.message || t('failedToTestVoiceConnection');
        setSettingsError(message);
        showToast(message, 'error');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToTestVoiceConnection');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const update = <K extends keyof typeof emptyVoiceSettingsForm>(key: K, value: (typeof emptyVoiceSettingsForm)[K]) =>
    setSettingsForm(prev => ({ ...prev, [key]: value }));

  const toggleWeekday = (day: number) => {
    const days = parseWeekdays(settingsForm.businessHoursWeekdays);
    if (days.has(day)) {
      days.delete(day);
    } else {
      days.add(day);
    }
    update('businessHoursWeekdays', formatWeekdays(days));
  };

  // ---- Derived status -------------------------------------------------------------------------

  const isInitialSetup = voiceChannelAccounts.accounts.length === 0;
  const hasPbxCredentials = Boolean(accountSettings?.hasClientId && accountSettings?.hasClientSecret);
  const pbxStatus: { tone: Tone; label: string } = latestTestResult
    ? latestTestResult.succeeded
      ? { tone: 'ok', label: t('callsStatusConnected') }
      : { tone: 'bad', label: t('callsStatusFailed') }
    : hasPbxCredentials
      ? { tone: 'warn', label: t('callsStatusNotTested') }
      : { tone: 'off', label: t('callsStatusMissingCredentials') };

  const selectedWeekdays = parseWeekdays(settingsForm.businessHoursWeekdays);
  const showBusinessHours = settingsForm.aiEnabled && settingsForm.aiAnswerMode === 'AfterHours';

  const twilioSidReady = Boolean(accountSettings?.voiceAgent?.hasTwilioAccountSid) || settingsForm.twilioAccountSid.trim().length > 0;
  const twilioTokenReady = Boolean(accountSettings?.voiceAgent?.hasTwilioAuthToken) || settingsForm.twilioAuthToken.trim().length > 0;
  const twilioNumberReady = settingsForm.twilioFromNumber.trim().length > 0;
  const twilioInUse = twilioSidReady || twilioTokenReady || twilioNumberReady;
  const twilioReady = twilioSidReady && twilioTokenReady && twilioNumberReady;
  const twilioStatus: { tone: Tone; label: string } = twilioReady
    ? { tone: 'ok', label: t('callsPathReady') }
    : twilioInUse
      ? { tone: 'warn', label: t('callsPathIncomplete') }
      : { tone: 'off', label: t('callsPathOff') };

  const effectiveRoutePointDn = settingsForm.threeCxRoutePointDn.trim() || accountSettings?.voiceAgent?.threeCxEffectiveRoutePointDn || '';
  const threeCxStatus: { tone: Tone; label: string } = settingsForm.threeCxAiEnabled
    ? hasPbxCredentials
      ? { tone: 'ok', label: effectiveRoutePointDn ? t('callsPathListening', { dn: effectiveRoutePointDn }) : t('callsPathReady') }
      : { tone: 'warn', label: t('callsPathIncomplete') }
    : { tone: 'off', label: t('callsPathOff') };

  const anyPathOn = (settingsForm.threeCxAiEnabled && hasPbxCredentials) || twilioReady;
  const handoffSet = settingsForm.handoffTarget.trim().length > 0;

  const readiness: Array<{ key: string; tone: 'ok' | 'warn' | 'info'; text: React.ReactNode }> = [
    {
      key: 'mode',
      tone: settingsForm.aiAnswerMode !== 'Off' ? 'ok' : 'warn',
      text: t('voiceAgentReadyMode', {
        mode: settingsForm.aiAnswerMode === 'AfterHours'
          ? t('voiceAgentModeAfterHours')
          : settingsForm.aiAnswerMode === 'Always'
            ? t('voiceAgentModeAlways')
            : t('voiceAgentModeOff'),
      }),
    },
    {
      key: 'path',
      tone: anyPathOn ? 'ok' : 'warn',
      text: anyPathOn ? t('voiceAgentReadyPathOn') : t('voiceAgentReadyPathOff'),
    },
    {
      key: 'handoff',
      tone: handoffSet ? 'ok' : 'warn',
      text: handoffSet ? t('voiceAgentReadyHandoffSet') : t('voiceAgentReadyHandoffMissing'),
    },
    {
      key: 'mute',
      tone: 'info',
      text: (
        <>
          {t('voiceAgentReadyMute')}{' '}
          <a href="/admin/companies">{t('voiceAgentReadyMuteLink')}</a>
        </>
      ),
    },
  ];

  const renderCopyField = (key: string, label: string, value: string, info?: string) => (
    <Field key={key} label={label} info={info}>
      {({ id }) => (
        <span className={styles.copyRow}>
          <input id={id} className="text-input" readOnly value={value} onFocus={event => event.target.select()} />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void copyValue(key, value); }}
          >
            {copiedField === key ? t('copied', { ns: 'calling' }) : t('copy', { ns: 'calling' })}
          </button>
        </span>
      )}
    </Field>
  );

  // ---- Render ---------------------------------------------------------------------------------

  return (
    <section className="company-card">
      <ChannelAccountBar
        channel="Voice"
        accounts={voiceChannelAccounts.accounts}
        limits={voiceChannelAccounts.limits}
        loading={voiceChannelAccounts.loading}
        error={voiceChannelAccounts.error}
        selectedAccountId={voiceChannelAccounts.selectedAccountId}
        onSelectAccount={voiceChannelAccounts.setSelectedAccountId}
        onRefresh={() => { void voiceChannelAccounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddAccount}
        onToggleAddForm={() => setShowAddAccount(current => !current)}
        onAccountUpdated={() => { void voiceChannelAccounts.reload(); }}
      />

      {showAddAccount && canManage ? (
        <p className={styles.muted} style={{ marginBottom: 16 }}>{t('channelAccountAddVoiceHelp')}</p>
      ) : null}

      {voiceChannelAccounts.loading && voiceChannelAccounts.accounts.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <div className={styles.intro}>
            <h3>{t('voiceIntegrationHeading')}</h3>
            <p className={styles.muted}>{t('voiceIntegrationHelp')}</p>
            <p className={`${styles.muted} ${styles.small}`}>
              {t('addonNotice', { ns: 'calling' })}{' '}
              <a href="/plans">{t('addonNoticeLink', { ns: 'calling' })}</a>
            </p>
          </div>

          {settingsError ? <div className="error">{settingsError}</div> : null}
          {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

          {loadingSettings ? <Spinner /> : (
            <>
              {/* Step 1 — PBX connection */}
              <div className={styles.step}>
                <div className={styles.stepHead}>
                  <div className={styles.stepTitle}>
                    <span className={styles.stepBadge}>1</span>
                    <h3>
                      {t('callsStepConnectTitle')}
                      <InfoTip text={t('callsStepConnectInfo')} />
                    </h3>
                  </div>
                  {!isInitialSetup ? <StatusPill tone={pbxStatus.tone}>{pbxStatus.label}</StatusPill> : null}
                </div>
                <p className={styles.stepHelp}>{t('callsStepConnectHelp')}</p>

                <div className="company-form-grid">
                  <Field label={t('voiceServerUrl')} info={t('voiceServerUrlInfo')}>
                    <input
                      className="text-input"
                      value={settingsForm.serverUrl}
                      onChange={event => update('serverUrl', event.target.value)}
                      placeholder="https://pbx.company.com"
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label={t('voiceDefaultCallerId')} info={t('voiceDefaultCallerIdInfo')}>
                    <input
                      className="text-input"
                      value={settingsForm.defaultCallerId}
                      onChange={event => update('defaultCallerId', event.target.value)}
                      placeholder="+99412..."
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label={t('voiceClientId')} info={t('voiceClientIdInfo')}>
                    <input
                      className="text-input"
                      value={settingsForm.clientId}
                      onChange={event => update('clientId', event.target.value)}
                      placeholder={accountSettings?.hasClientId ? t('voiceCredentialConfigured') : ''}
                      disabled={!canManage}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label={t('voiceClientSecret')} info={t('voiceClientSecretInfo')}>
                    <input
                      className="text-input"
                      type="password"
                      value={settingsForm.clientSecret}
                      onChange={event => update('clientSecret', event.target.value)}
                      placeholder={accountSettings?.hasClientSecret ? t('voiceCredentialConfigured') : ''}
                      disabled={!canManage}
                      autoComplete="new-password"
                    />
                  </Field>
                  <ToggleField
                    label={t('voiceInboundEnabled')}
                    info={t('voiceInboundEnabledInfo')}
                    checked={settingsForm.inboundEnabled}
                    onChange={value => update('inboundEnabled', value)}
                    disabled={!canManage}
                    fullWidth={false}
                  />
                  <ToggleField
                    label={t('voiceOutboundEnabled')}
                    info={t('voiceOutboundEnabledInfo')}
                    checked={settingsForm.outboundEnabled}
                    onChange={value => update('outboundEnabled', value)}
                    disabled={!canManage}
                    fullWidth={false}
                  />
                </div>

                <div className={styles.credentialStatus}>
                  <span>{t('voiceClientId')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasClientId)} /></span>
                  <span>{t('voiceClientSecret')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasClientSecret)} /></span>
                </div>

                {canManage && !isInitialSetup ? (
                  <div className={styles.inlineActions}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { void testConnection(); }}
                      disabled={testingConnection || !voiceChannelAccounts.selectedAccount}
                    >
                      {testingConnection ? t('savingEllipsis', { ns: 'admin' }) : t('testVoiceConnection')}
                    </button>
                    {latestTestResult ? (
                      <p className={`${styles.testResult} ${latestTestResult.succeeded ? styles.testOk : styles.testBad}`}>
                        {latestTestResult.message}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {/* Step 2 — screen pop for employees */}
              {popSettings ? (
                <div className={styles.step}>
                  <div className={styles.stepHead}>
                    <div className={styles.stepTitle}>
                      <span className={styles.stepBadge}>2</span>
                      <h3>
                        {t('inboundPopHeading', { ns: 'calling' })}
                        <InfoTip text={t('callsStepPopInfo')} />
                      </h3>
                    </div>
                    <StatusPill tone={settingsForm.inboundEnabled ? 'ok' : 'off'}>
                      {settingsForm.inboundEnabled ? t('callsPathOn') : t('callsPathOff')}
                    </StatusPill>
                  </div>
                  <p className={styles.stepHelp}>{t('inboundPopDescription', { ns: 'calling' })}</p>
                  <div className="company-form-grid">
                    {renderCopyField('popUrl', t('inboundPopUrl', { ns: 'calling' }), popSettings.popUrlTemplate, t('callsPopUrlInfo'))}
                    {renderCopyField('lookupUrl', t('inboundPopLookupUrl', { ns: 'calling' }), popSettings.lookupUrlTemplate, t('callsPopLookupUrlInfo'))}
                    {renderCopyField('secret', t('inboundPopSecret', { ns: 'calling' }), popSettings.crmLookupSecret, t('callsPopSecretInfo'))}
                  </div>
                  <p className={`${styles.muted} ${styles.small}`} style={{ marginTop: 8 }}>
                    {t('inboundPopSecretHint', { ns: 'calling' })}
                  </p>
                </div>
              ) : null}

              {/* Step 3 — AI answering */}
              {!isInitialSetup ? (
                <div className={styles.step}>
                  <div className={styles.stepHead}>
                    <div className={styles.stepTitle}>
                      <span className={styles.stepBadge}>{popSettings ? 3 : 2}</span>
                      <h3>
                        {t('voiceAgentHeading')}
                        <InfoTip text={t('voiceAgentHelp')} />
                      </h3>
                    </div>
                    <StatusPill tone={settingsForm.aiEnabled ? (anyPathOn ? 'ok' : 'warn') : 'off'}>
                      {settingsForm.aiEnabled
                        ? anyPathOn ? t('callsPathOn') : t('callsPathIncomplete')
                        : t('callsPathOff')}
                    </StatusPill>
                  </div>
                  <p className={styles.stepHelp}>{t('callsStepAiHelp')}</p>

                  <ToggleField
                    label={t('voiceAgentEnabled')}
                    info={t('voiceAgentEnabledInfo')}
                    checked={settingsForm.aiEnabled}
                    onChange={value => setSettingsForm(prev => ({
                      ...prev,
                      aiEnabled: value,
                      aiAnswerMode: value && prev.aiAnswerMode === 'Off' ? 'Always' : prev.aiAnswerMode,
                    }))}
                    disabled={!canManage}
                  />

                  {!settingsForm.aiEnabled ? (
                    <p className={styles.disabledNote} style={{ marginTop: 12 }}>{t('voiceAgentDisabledHint')}</p>
                  ) : (
                    <>
                      <div className={styles.subHeading}>
                        {t('voiceAgentReadinessHeading')}
                        <InfoTip text={t('voiceAgentReadinessInfo')} />
                      </div>
                      <div className={styles.readiness}>
                        {readiness.map(item => (
                          <div key={item.key} className={styles.readinessItem}>
                            <span
                              className={`${styles.readinessIcon} ${
                                item.tone === 'ok' ? styles.readinessOk : item.tone === 'warn' ? styles.readinessWarn : styles.readinessInfo
                              }`}
                              aria-hidden="true"
                            >
                              {item.tone === 'ok' ? '✓' : item.tone === 'warn' ? '!' : 'i'}
                            </span>
                            <span>{item.text}</span>
                          </div>
                        ))}
                      </div>

                      <div className={styles.subHeading}>
                        {t('voiceAgentRulesHeading')}
                        <InfoTip text={t('voiceAgentRulesInfo')} />
                      </div>
                      <div className="company-form-grid">
                        <Field label={t('voiceAgentMode')} info={t('voiceAgentModeInfo')}>
                          <select
                            className="text-input"
                            value={settingsForm.aiAnswerMode}
                            onChange={event => update('aiAnswerMode', event.target.value)}
                            disabled={!canManage}
                          >
                            <option value="Always">{t('voiceAgentModeAlways')}</option>
                            <option value="AfterHours">{t('voiceAgentModeAfterHours')}</option>
                            <option value="Off">{t('voiceAgentModeOff')}</option>
                          </select>
                        </Field>
                        <Field
                          label={t('voiceAgentHandoffTarget')}
                          info={t('voiceAgentHandoffTargetInfo')}
                          hint={t('voiceAgentHandoffTargetHint')}
                        >
                          <input
                            className="text-input"
                            value={settingsForm.handoffTarget}
                            onChange={event => update('handoffTarget', event.target.value)}
                            placeholder="100 or +1555... or sip:100@pbx"
                            disabled={!canManage}
                          />
                        </Field>
                        <Field
                          label={t('voiceAgentAnsweredNumbers')}
                          info={t('voiceAgentAnsweredNumbersInfo')}
                          hint={t('voiceAgentAnsweredNumbersHint')}
                        >
                          <input
                            className="text-input"
                            value={settingsForm.aiAnsweredNumbers}
                            onChange={event => update('aiAnsweredNumbers', event.target.value)}
                            placeholder="+15551230000, +15551239999"
                            disabled={!canManage}
                          />
                        </Field>
                        <Field label={t('voiceAgentGreeting')} info={t('voiceAgentGreetingInfo')}>
                          <input
                            className="text-input"
                            value={settingsForm.greeting}
                            onChange={event => update('greeting', event.target.value)}
                            placeholder={t('voiceAgentGreetingPlaceholder')}
                            disabled={!canManage}
                          />
                        </Field>
                      </div>

                      {showBusinessHours ? (
                        <>
                          <div className={styles.subHeading}>
                            {t('voiceAgentBusinessHoursHeading')}
                            <InfoTip text={t('voiceAgentBusinessHoursInfo')} />
                          </div>
                          <div className="company-form-grid">
                            <Field label={t('voiceAgentBusinessHoursStart')}>
                              <input
                                className="text-input"
                                type="time"
                                value={settingsForm.businessHoursStart}
                                onChange={event => update('businessHoursStart', event.target.value)}
                                disabled={!canManage}
                              />
                            </Field>
                            <Field label={t('voiceAgentBusinessHoursEnd')}>
                              <input
                                className="text-input"
                                type="time"
                                value={settingsForm.businessHoursEnd}
                                onChange={event => update('businessHoursEnd', event.target.value)}
                                disabled={!canManage}
                              />
                            </Field>
                            <Field
                              label={t('voiceAgentBusinessHoursTimeZone')}
                              info={t('voiceAgentBusinessHoursTimeZoneInfo')}
                              hint={t('voiceAgentBusinessHoursTimeZoneHint')}
                            >
                              {({ id, hintId }) => (
                                <>
                                  <span className={styles.tzRow}>
                                    <input
                                      id={id}
                                      className="text-input"
                                      list="calls-time-zones"
                                      aria-describedby={hintId}
                                      value={settingsForm.businessHoursTimeZone}
                                      onChange={event => update('businessHoursTimeZone', event.target.value)}
                                      placeholder="Asia/Baku"
                                      disabled={!canManage}
                                    />
                                    {canManage && getBrowserTimeZone() ? (
                                      <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => update('businessHoursTimeZone', getBrowserTimeZone())}
                                      >
                                        {t('voiceAgentUseMyTimeZone')}
                                      </button>
                                    ) : null}
                                  </span>
                                  {knownTimeZones.length > 0 ? (
                                    <datalist id="calls-time-zones">
                                      {knownTimeZones.map(zone => <option key={zone} value={zone} />)}
                                    </datalist>
                                  ) : null}
                                </>
                              )}
                            </Field>
                            <Field label={t('voiceAgentBusinessHoursWeekdays')} info={t('voiceAgentBusinessHoursWeekdaysInfo')}>
                              {({ labelId }) => (
                                <span className={styles.dayChips} role="group" aria-labelledby={labelId}>
                                  {ISO_WEEKDAYS.map((day, index) => (
                                    <button
                                      key={day}
                                      type="button"
                                      className={`${styles.dayChip} ${selectedWeekdays.has(day) ? styles.dayChipOn : ''}`}
                                      aria-pressed={selectedWeekdays.has(day)}
                                      onClick={() => toggleWeekday(day)}
                                      disabled={!canManage}
                                    >
                                      {weekdayLabels[index]}
                                    </button>
                                  ))}
                                </span>
                              )}
                            </Field>
                          </div>
                        </>
                      ) : null}

                      <div className={styles.subHeading}>
                        {t('voiceAgentPathsHeading')}
                        <InfoTip text={t('voiceAgentPathsInfo')} />
                      </div>
                      <p className={`${styles.muted} ${styles.small}`} style={{ marginBottom: 10 }}>{t('voiceAgentPathsHelp')}</p>

                      <div className={styles.paths}>
                        {/* Native 3CX path */}
                        <div className={`${styles.pathCard} ${settingsForm.threeCxAiEnabled ? styles.pathCardActive : ''}`}>
                          <div className={styles.pathHead}>
                            <h4>
                              {t('voiceAgentThreeCxHeading')}
                              <InfoTip text={t('voiceAgentThreeCxHelp')} />
                            </h4>
                            <StatusPill tone={threeCxStatus.tone}>{threeCxStatus.label}</StatusPill>
                          </div>
                          <ToggleField
                            label={t('voiceAgentThreeCxEnabled')}
                            info={t('voiceAgentThreeCxEnabledInfo')}
                            checked={settingsForm.threeCxAiEnabled}
                            onChange={value => update('threeCxAiEnabled', value)}
                            disabled={!canManage}
                          />
                          <div className={styles.fields}>
                            <Field
                              label={t('voiceAgentThreeCxRoutePointDn')}
                              info={t('voiceAgentThreeCxRoutePointDnInfo')}
                              hint={t('voiceAgentThreeCxRoutePointDnHint')}
                            >
                              <input
                                className="text-input"
                                value={settingsForm.threeCxRoutePointDn}
                                onChange={event => update('threeCxRoutePointDn', event.target.value)}
                                placeholder={accountSettings?.voiceAgent?.threeCxEffectiveRoutePointDn || t('voiceAgentThreeCxRoutePointDnPlaceholder')}
                                disabled={!canManage}
                              />
                            </Field>
                          </div>
                          <p className={`${styles.muted} ${styles.small}`}>{t('voiceAgentThreeCxLicenseHint')}</p>
                        </div>

                        {/* Twilio path */}
                        <div className={`${styles.pathCard} ${twilioInUse ? styles.pathCardActive : ''}`}>
                          <div className={styles.pathHead}>
                            <h4>
                              {t('voiceAgentTwilioHeading')}
                              <InfoTip text={t('voiceAgentTwilioHelp')} align="end" />
                            </h4>
                            <StatusPill tone={twilioStatus.tone}>{twilioStatus.label}</StatusPill>
                          </div>
                          <div className={styles.fields}>
                            <Field label={t('voiceAgentTwilioAccountSid')} info={t('voiceAgentTwilioAccountSidInfo')}>
                              <input
                                className="text-input"
                                value={settingsForm.twilioAccountSid}
                                onChange={event => update('twilioAccountSid', event.target.value)}
                                placeholder={accountSettings?.voiceAgent?.hasTwilioAccountSid ? t('voiceCredentialConfigured') : 'ACxxxxxxxx'}
                                disabled={!canManage}
                                autoComplete="off"
                              />
                            </Field>
                            <Field label={t('voiceAgentTwilioAuthToken')} info={t('voiceAgentTwilioAuthTokenInfo')}>
                              <input
                                className="text-input"
                                type="password"
                                value={settingsForm.twilioAuthToken}
                                onChange={event => update('twilioAuthToken', event.target.value)}
                                placeholder={accountSettings?.voiceAgent?.hasTwilioAuthToken ? t('voiceCredentialConfigured') : ''}
                                disabled={!canManage}
                                autoComplete="new-password"
                              />
                            </Field>
                            <Field label={t('voiceAgentTwilioFromNumber')} info={t('voiceAgentTwilioFromNumberInfo')}>
                              <input
                                className="text-input"
                                value={settingsForm.twilioFromNumber}
                                onChange={event => update('twilioFromNumber', event.target.value)}
                                placeholder="+1555..."
                                disabled={!canManage}
                              />
                            </Field>
                          </div>
                          <div className={styles.credentialStatus}>
                            <span>{t('voiceAgentTwilioAccountSid')}: <ConnectorFieldStatus done={Boolean(accountSettings?.voiceAgent?.hasTwilioAccountSid)} /></span>
                            <span>{t('voiceAgentTwilioAuthToken')}: <ConnectorFieldStatus done={Boolean(accountSettings?.voiceAgent?.hasTwilioAuthToken)} /></span>
                          </div>
                          {accountSettings?.voiceAgent?.incomingWebhookUrl ? (
                            <div className={styles.fields}>
                              {renderCopyField('voiceIncoming', t('voiceAgentIncomingWebhook'), accountSettings.voiceAgent.incomingWebhookUrl, t('voiceAgentIncomingWebhookInfo'))}
                              {accountSettings.voiceAgent.statusCallbackUrl
                                ? renderCopyField('voiceStatus', t('voiceAgentStatusCallback'), accountSettings.voiceAgent.statusCallbackUrl, t('voiceAgentStatusCallbackInfo'))
                                : null}
                              <p className={`${styles.muted} ${styles.small}`}>{t('voiceAgentTwilioHint')}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}

          {canManage ? (
            <div className={styles.actions}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { void saveSettings(); }}
                disabled={savingSettings || loadingSettings}
              >
                {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveVoiceSettings')}
              </button>
              {isInitialSetup ? (
                <span className={`${styles.muted} ${styles.small}`}>{t('callsInitialSetupHint')}</span>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
